import type { BuildContext } from "../types.js";
import { scaleFor } from "../format.js";
import { backgroundGradient } from "../primitives/backgroundGradient.js";
import { brandLogo } from "../primitives/brandLogo.js";
import { hookText } from "../primitives/hookText.js";
import { cta } from "../primitives/cta.js";
import { safeArea } from "../primitives/safeArea.js";
import { getLocale, type LocaleCode } from "../../locales/index.js";
import { hookRevealSchema, type HookRevealProps } from "../../templates/index.js";
import { assemble } from "./shared.js";

export const HOOK_REVEAL_DURATION_SEC = 8;

export function buildHookRevealHtml(
  rawProps: unknown,
  ctx: Omit<BuildContext, "durationSec">,
): string {
  const props = hookRevealSchema.parse(rawProps) as HookRevealProps;
  const loc = getLocale(ctx.locale as LocaleCode);
  const ctaText = props.cta || loc.ctas[props.ctaKey as keyof typeof loc.ctas];

  const fullCtx: BuildContext = { ...ctx, durationSec: HOOK_REVEAL_DURATION_SEC };

  const hookSize = scaleFor(ctx.format, { base: 110, short: 130, square: 100, wide: 130 });
  const payoffSize = scaleFor(ctx.format, { base: 64, short: 72, square: 60, wide: 72 });
  const ctaSize = scaleFor(ctx.format, { base: 56, short: 64, square: 50, wide: 60 });
  const logoSize = scaleFor(ctx.format, { base: 90, short: 110, square: 90, wide: 100 });

  const bg = backgroundGradient(fullCtx, "bg", { variant: "primary", animated: true });

  const hookStart = 6 / 30;
  const hookDur = 75 / 30;
  const payoffStart = 75 / 30;
  const payoffDur = 75 / 30;
  const ctaStart = 150 / 30;
  const ctaDur = 60 / 30;

  const logoFrag = brandLogo({ id: "tpl-logo", size: logoSize, color: "white" });
  const hookFrag = hookText({ id: "tpl-hook", text: props.hook, startSec: hookStart, size: hookSize, color: "#fff" });
  const payoffFrag = hookText({ id: "tpl-payoff", text: props.payoff, startSec: payoffStart, size: payoffSize, color: "#fff" });
  const ctaFrag = cta({ id: "tpl-cta", text: ctaText, startSec: ctaStart, durationSec: ctaDur, size: ctaSize });

  const innerHtml = `
<div id="tpl-logo-row" style="display:flex;justify-content:flex-start;">
  ${logoFrag.html}
</div>
<div id="tpl-stack" style="flex:1;display:flex;flex-direction:column;justify-content:center;gap:24px;">
  <div id="tpl-hook-clip" class="clip" data-start="${hookStart}" data-duration="${hookDur}" data-track-index="0">
    ${hookFrag.html}
  </div>
  <div id="tpl-payoff-clip" class="clip" data-start="${payoffStart}" data-duration="${payoffDur}" data-track-index="1">
    ${payoffFrag.html}
  </div>
</div>
<div id="tpl-cta-clip" class="clip" data-start="${ctaStart}" data-duration="${ctaDur}" data-track-index="2" style="display:flex;justify-content:flex-start;">
  ${ctaFrag.html}
</div>
`;

  const safe = safeArea({ id: "tpl-safe", format: ctx.format, childrenHtml: innerHtml });

  return assemble({
    templateId: "HookReveal",
    ctx: fullCtx,
    background: bg,
    contentFragments: [logoFrag, hookFrag, payoffFrag, ctaFrag],
    safeAreaInnerHtml: innerHtml,
    safeArea: safe,
  });
}
